// src/pages/ServiceDetails.js

import React, { useEffect, useState } from "react";
import api from "../services/api";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { Box, Paper, Typography, Button, Toolbar as MuiToolbar, TextField, Grid } from "@mui/material";
import ImageGalleryDropdown from "../components/ImageGalleryDropdown";
import { formatDate, formatTime, parseDate, parseTime } from '../utils/dateTimeUtils';
import RecordNavigator from "../components/RecordNavigator";

const ServiceDetails = () => {
  const { id } = useParams(); // route: /services/:id
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [editedService, setEditedService] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const [services, setServices] = useState(location.state?.records || []);

  useEffect(() => {
    async function fetchServiceDetails() {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No token found");
        // Fetch the service details. The backend should include the pet and owner and bucket relations.
        const response = await api.get(`/services/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const serviceData = response.data;
        setService(serviceData);
        setEditedService(serviceData);
      } catch (error) {
        console.error("Error fetching service details:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchServiceDetails();
  }, [id]);

  useEffect(() => {
    if (!services.length) {
      console.warn("No services found in state. Falling back to fetch pet-specific services.");
      async function fetchPetServices() {
        try {
          const token = localStorage.getItem("token");
          const response = await api.get(`/pets/${service?.pet?.id}/services`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setServices(response.data.data);
        } catch (error) {
          console.error("Error fetching pet-specific services:", error);
        }
      }
      if (service?.pet?.id) {
        fetchPetServices();
      }
    }
  }, [services, service]);

  useEffect(() => {
    if (service) {
      setEditedService(service);
    }
  }, [service]);

  if (loading) {
    return <Typography>Loading service details...</Typography>;
  }

  if (!service) {
    return <Typography>Service not found.</Typography>;
  }

  // Handle input changes in the edit form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedService({ ...editedService, [name]: value });
  };

  // Toggle edit mode or save changes if already editing
  const handleEditClick = async () => {
    if (isEditing) {
      try {
        const token = localStorage.getItem("token");
        const updatePayload = {
          date: parseDate(editedService.date),
          time: parseTime(editedService.time),
          service: editedService.service,
          amount: Number(editedService.amount),
          comments: editedService.comments,
          // If you allow updating groomer, include it; otherwise, keep it read-only
        };
        //console.log("Sending update payload:", updatePayload);
        const response = await api.put(`/services/${id}`, updatePayload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data) {
          setService(prev => ({
            ...response.data,
            bucket: prev.bucket || response.data.bucket,
          }));

          setEditedService(prev => ({
            ...response.data,
            bucket: prev.bucket || response.data.bucket,
          }));

          setIsEditing(false);
        } else {
          console.error("No updated data received from API.");
        }
      } catch (error) {
        console.error("Error saving changes:", error.response ? error.response.data : error);
      }
    } else {
      // Before enabling editing, update the time field to show the friendly format
      setEditedService(prev => ({
        ...prev,
        time: prev.time ? prev.time.slice(0, 5) : ""
      }));
      setIsEditing(true);
    }
  };

  // Delete service with confirmation
  const handleDeleteClick = () => {
    if (window.confirm("Are you sure you want to delete this service? This action cannot be undone.")) {
      const token = localStorage.getItem("token");
      api
        .delete(`/services/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then(() => {
          navigate(`/customers/${service.pet.owner.id}`);
        })
        .catch((error) => {
          console.error("Error deleting service:", error);
        });
    }
  };

  const disabledInputStyles = {
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: "black",
    },
  };

  return (
    <>
      <MuiToolbar />
      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        <Paper sx={{ p: 2 }}>
          {/* Header: Customer Name > Pet Name > Service Details */}
          <Typography variant="h5" gutterBottom sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>
              <Link to={`/customers/${service.pet.owner.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                {service.pet.owner.firstName} {service.pet.owner.lastName}
              </Link>{" > "}
              <Link to={`/pets/${service.pet.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                {service.pet.name}
              </Link>{" > "} Service Details
            </span>
            <RecordNavigator records={services} currentId={id} basePath="/services" />
          </Typography>

          {/* Service Details Section */}
          <Box sx={{ maxWidth: '80%', mx: 'auto' }}>
            <Grid container spacing={2} justifyContent="center">
              {/* Row 1: Date, Time, Service, Amount */}
              <Grid item xs={12} sm={2}>
                <TextField
                  label="Date"
                  name="date"
                  type={isEditing ? "date" : ""}
                  value={isEditing ? editedService.date || "" : formatDate(editedService.date)}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  disabled={!isEditing}
                  sx={disabledInputStyles}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  label="Time"
                  name="time"
                  type={isEditing ? "time" : ""}
                  value={isEditing ? editedService.time : formatTime(editedService.time) || ""}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  disabled={!isEditing}
                  sx={disabledInputStyles}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Service"
                  name="service"
                  value={editedService.service || ""}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  disabled={!isEditing}
                  sx={disabledInputStyles}
                />
              </Grid>
              <Grid item xs={12} sm={1}>
                <TextField
                  label="Amount"
                  name="amount"
                  value={editedService.amount || ""}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  disabled={!isEditing}
                  sx={disabledInputStyles}
                />
              </Grid>

              {/* Row 2: Groomer, Comments, Photo Upload Icon */}
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Groomer"
                  name="groomer"
                  value={service.groomer ? `${service.groomer.firstName} ${service.groomer.lastName}` : ""}
                  fullWidth
                  margin="normal"
                  disabled={!isEditing}
                  sx={disabledInputStyles}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  label="Comments"
                  name="comments"
                  value={editedService.comments || ""}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  multiline
                  rows={2}
                  disabled={!isEditing}
                  sx={disabledInputStyles}
                />
              </Grid>
              <Grid item xs={12} sm={1} sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ImageGalleryDropdown
                  images={editedService.bucket?.images || []}
                  onUploadSuccess={(newImageData) => {
                    setEditedService(prev => ({
                      ...prev,
                      bucket: {
                        ...prev.bucket,
                        images: [...(prev.bucket?.images || []), newImageData]
                      }
                    }));
                  }}
                  onDelete={(imageId) => {
                    api.delete(`/images/${imageId}`)
                      .then(() => {
                        setEditedService(prev => ({
                          ...prev,
                          bucket: {
                            ...prev.bucket,
                            images: prev.bucket.images.filter(img => img.id !== imageId)
                          }
                        }));
                      })
                      .catch(error => {
                        console.error("Error deleting image:", error);
                      });
                  }}
                  serviceId={service.id}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', maxWidth: '60%', mx: 'auto' }}>
            <Button variant="contained" color="primary" onClick={handleEditClick}>
              {isEditing ? "Save Changes" : "Edit Service"}
            </Button>
            <Button variant="outlined" color="error" onClick={handleDeleteClick}>
              Delete Service
            </Button>
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default ServiceDetails;